import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import {useInfiniteQuery} from '@tanstack/react-query';
import moment from 'moment';
import React from 'react';
import {Column, useFlexLayout, useTable} from 'react-table';

import {api} from 'app/modules/Remote';
import type {ApiCardPurchase} from 'app/modules/Remote/Organization';
import * as conversionUtils from 'app/utils/conversionUtils';
import {formatCost} from 'app/utils/costUtils';

import cs from './ContractHistory.module.scss';
import PagedTable from './PagedTable';
import {ROWS_PER_PAGE} from './utils';

const DATE_FORMAT = 'MM/DD/YYYY';

const NUMERIC_COLUMNS = new Set(['sceneCount', 'billedAcres', 'totalCents']);

// Missing for a checkout from before totals were saved, or from an API that predates them. The invoice
// itself can still be opened.
const formatInvoiceAmount = (cents: number | null | undefined) =>
  cents == null ? '—' : formatCost(cents);

function useCardPurchases(organizationId: string) {
  return useInfiniteQuery({
    queryKey: ['cardPurchases', organizationId],
    queryFn: ({pageParam}) =>
      api.organizations.cardPurchases.list(organizationId, pageParam, ROWS_PER_PAGE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === ROWS_PER_PAGE ? allPages.length + 1 : undefined,
    select: (data) => data.pages.flat(),
  });
}

async function openInvoice(organizationId: string, checkoutKey: string) {
  // Opened before the request so it counts as the click's own popup: one opened after an await is
  // blocked. Stripe's links expire, so the URL is only known once the request comes back.
  const tab = window.open('', '_blank');
  try {
    const {hostedInvoiceUrl} = await api.organizations.cardPurchases.getInvoice(
      organizationId,
      checkoutKey
    );
    if (tab) {
      tab.opener = null;
      tab.location.href = hostedInvoiceUrl;
    } else {
      window.open(hostedInvoiceUrl, '_blank', 'noopener');
    }
  } catch (error) {
    tab?.close();
    if ((error as {status?: number} | null)?.status === 404) {
      window.alert('This invoice is no longer available. Contact us for a copy.');
    } else {
      Sentry.captureException(error);
      window.alert('We weren’t able to open this invoice right now. Please try again.');
    }
  }
}

export const CardPurchasesTable: React.FC<{organizationId: string}> = ({organizationId}) => {
  const {data, status, hasNextPage, fetchNextPage, isFetchingNextPage} =
    useCardPurchases(organizationId);
  const [openingKeys, setOpeningKeys] = React.useState<ReadonlySet<string>>(new Set());

  const onViewInvoice = React.useCallback(
    async (checkoutKey: string) => {
      setOpeningKeys((previous) => new Set(previous).add(checkoutKey));
      try {
        await openInvoice(organizationId, checkoutKey);
      } finally {
        setOpeningKeys((previous) => {
          const next = new Set(previous);
          next.delete(checkoutKey);
          return next;
        });
      }
    },
    [organizationId]
  );

  const columns = React.useMemo<Column<ApiCardPurchase>[]>(
    () => [
      {
        Header: 'Date',
        accessor: 'createdAt',
        Cell: ({cell}) => moment(cell.value as string).format(DATE_FORMAT),
        width: 60,
      },
      {Header: 'Ordered by', accessor: 'orderedBy'},
      {
        Header: 'Note',
        accessor: 'note',
        // The cell truncates a long note, so its full text is on hover.
        Cell: ({cell}) => <span title={cell.value ?? undefined}>{cell.value}</span>,
      },
      {Header: 'Scenes', accessor: 'sceneCount', width: 50},
      {
        Header: 'Billed acres',
        accessor: 'billedAcres',
        Cell: ({cell}) => conversionUtils.numberWithCommas((cell.value as number).toString()),
        width: 60,
      },
      {
        Header: 'Total',
        accessor: 'totalCents',
        Cell: ({cell}) => formatInvoiceAmount(cell.value as number | null),
        width: 60,
      },
      {
        Header: 'Invoice',
        id: 'invoice',
        accessor: (purchase) => purchase,
        Cell: ({cell}) => {
          const {checkoutKey} = cell.value as ApiCardPurchase;
          return (
            <div className={cs.contractCell}>
              <B.Button
                small
                minimal
                intent={B.Intent.PRIMARY}
                icon="document-open"
                loading={openingKeys.has(checkoutKey)}
                onClick={() => onViewInvoice(checkoutKey)}
              >
                View invoice
              </B.Button>
            </div>
          );
        },
        width: 90,
      },
    ],
    [onViewInvoice, openingKeys]
  );

  const instance = useTable({columns, data: data ?? []}, useFlexLayout);

  if (status === 'pending') {
    return (
      <div className={cs.loadingContainer}>
        <B.Spinner />
      </div>
    );
  }
  if (status === 'error') {
    return <B.NonIdealState icon="error" title="Couldn’t load receipts" />;
  }
  if (instance.rows.length === 0) {
    return (
      <B.NonIdealState
        icon="document"
        title="No receipts yet"
        description="Receipts are available for purchases paid at checkout."
      />
    );
  }

  return (
    <PagedTable
      instance={instance}
      numericColumns={NUMERIC_COLUMNS}
      hasUnloadedRows={hasNextPage}
      loadMore={fetchNextPage}
      loadingMore={isFetchingNextPage}
    />
  );
};

export default CardPurchasesTable;
